import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { motion } from 'framer-motion'; // ⭐️ Import motion

// ⭐️ Animation Variants
const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.15, // Delay between each child animation
      delayChildren: 0.2,    // Initial delay for the entire block
    },
  },
};

const textVariant = {
  initial: { y: 30, opacity: 0 },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15,
    },
  },
};

const imageVariant = {
  initial: { scale: 0.9, opacity: 0 },
  animate: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.17, 0.55, 0.55, 1], // Smooth custom easing
      delay: 0.4, // Delay slightly after the text starts
    },
  },
};

export default function HeroSection() {
  return (
    // Outer Container
    <section className="relative bg-white pt-12 pb-20 md:pt-24 md:pb-32 min-h-[90vh] flex items-center overflow-hidden">
      {/* Decorative Background Shape */}
      <div className="absolute inset-y-0 right-0 w-full md:w-1/2 bg-gray-50 transform skew-x-[-12deg] origin-bottom-right hidden md:block"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Main Grid Container */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
          
          {/* ⭐️ Hero Content - Animated Left Side */}
          <motion.div
            className="md:max-w-xl mx-auto lg:mx-0 text-center lg:text-left"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >

            {/* Subtitle/Pre-header */}
            <motion.span 
                className="inline-block text-orange-600 text-sm font-semibold px-7 py-1 sm:py-3.5 rounded-full tracking-wider mb-6 pt-10"
                variants={textVariant}
            >
              NEW LIVING
            </motion.span>

            {/* Headline */}
            <motion.h1 
                className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-gray-900"
                variants={textVariant}
            >
              <span className="block leading-tight">FIND YOUR SPACE.</span>
              <span className="block text-orange-600 leading-tight">SIMPLIFIED.</span>
            </motion.h1>

            {/* Decorative Separator */}
            <motion.div 
                className="h-1 w-20 bg-orange-500 rounded-full mx-auto lg:mx-0 my-6"
                variants={textVariant}
            ></motion.div>

            {/* Body Text */}
            <motion.p 
                className="mt-3 text-base sm:text-lg text-gray-600 max-w-md mx-auto lg:mx-0"
                variants={textVariant}
            >
              Your trusted provider in simple and affordable living. We connect you with the perfect home without the hassle or high fees.
            </motion.p>

            {/* CTA Button */}
            <motion.div 
                className="mt-8 flex justify-center lg:justify-start"
                variants={textVariant}
            >
              <Link
                to="/properties"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-xl text-white bg-black hover:bg-gray-600 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-orange-500 focus:ring-opacity-50"
              >
                <Search className="w-5 h-5 mr-3" />
                Browse properties
              </Link>
            </motion.div>
          </motion.div>


          {/* ⭐️ Hero Image - Animated Right Side */}
          <div className="mt-12 lg:mt-0 relative flex justify-center lg:justify-end">
            <motion.div
                className="w-full max-w-md md:max-w-lg lg:max-w-none lg:w-full h-80 sm:h-96 lg:h-[550px] relative rounded-3xl shadow-2xl overflow-hidden"
                variants={imageVariant}
                initial="initial"
                animate="animate"
            >
              <img
                className="absolute inset-0 w-full h-full object-cover"
                src=" https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                alt="Modern, spacious home interior with large windows and a sofa"
                loading="lazy"
              />
              {/* Optional: Add a subtle overlay for effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/10 to-transparent"></div>
            </motion.div>
          </div>
          
        </div>

      </div>
    </section>
  );
}